import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from './firebaseConfig';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Telas principais
import Login from './Login';
import Dashboard from './Dashboard';
import MapaChales from './MapaChales';
import OcupacaoMapa from './OcupacaoMapa';
import Hospedes from './Hospedes';
import Manutencao from './Manutencao';
import Tarifas from './Tarifas';
import Relatorios from './Relatorios';
import Usuarios from './Usuarios';
import Pacotes from './Pacotes';
import Configuracoes from './Configuracoes';
import DashboardStatus from './DashboardStatus'; // <<<--- 1. NOVO COMPONENTE IMPORTADO

// Componentes MUI para o Layout
import { 
  AppBar, Toolbar, Typography, Drawer, List, ListItem, ListItemButton, ListItemIcon, 
  ListItemText, Box, CircularProgress, CssBaseline, Divider
} from '@mui/material';

// Ícones do Menu
import DashboardIcon from '@mui/icons-material/Dashboard';
import MapIcon from '@mui/icons-material/Map';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PeopleIcon from '@mui/icons-material/People';
import BuildIcon from '@mui/icons-material/Build';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';

const drawerWidth = 240;

// Componente de Layout Principal
function MainLayout() {
  const location = useLocation();

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
    }
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    // <<<--- 2. NOVO ITEM DE MENU ADICIONADO AQUI ---
    { text: 'Status dos Chalés', icon: <DashboardIcon />, path: '/status-chales' },
    { text: 'Relatórios', icon: <AssessmentIcon />, path: '/relatorios' },
    { divider: true },
    { text: 'Mapa do Dia', icon: <MapIcon />, path: '/mapa-do-dia' },
    { text: 'Mapa de Ocupação', icon: <CalendarMonthIcon />, path: '/' },
    { divider: true },
    { text: 'Hóspedes', icon: <PeopleIcon />, path: '/hospedes' },
    { text: 'Manutenções', icon: <BuildIcon />, path: '/manutencoes' },
    { text: 'Pacotes', icon: <CardGiftcardIcon />, path: '/pacotes' },
    { text: 'Tarifas', icon: <MonetizationOnIcon />, path: '/tarifas' },
    { divider: true },
    { text: 'Usuários', icon: <SupervisorAccountIcon />, path: '/usuarios' },
    { text: 'Configurações', icon: <SettingsIcon />, path: '/configuracoes' }, 
  ];

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" sx={{ width: `calc(100% - ${drawerWidth}px)`, ml: `${drawerWidth}px` }}>
        <Toolbar>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Sistema de Gestão AABB
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2">
              {auth.currentUser?.email}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        sx={{ width: drawerWidth, flexShrink: 0, '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box' } }}
        variant="permanent"
        anchor="left"
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto' }}>
          <List>
            {menuItems.map((item, index) => (
              item.divider ? <Divider key={`divider-${index}`} sx={{ my: 1 }} /> : (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton component={Link} to={item.path} selected={location.pathname === item.path}>
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              )
            ))}
          </List>
        </Box>
        <Box sx={{ marginTop: 'auto' }}>
          <List>
            <ListItem disablePadding>
              <ListItemButton onClick={handleLogout}>
                <ListItemIcon><LogoutIcon /></ListItemIcon>
                <ListItemText primary="Sair" />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.default', p: 3 }}>
        <Toolbar />
        <Routes>
          <Route path="/" element={<OcupacaoMapa />} />
          <Route path="/dashboard" element={<Dashboard />} />
          {/* <<<--- 3. NOVA ROTA ADICIONADA AQUI --- */}
          <Route path="/status-chales" element={<DashboardStatus />} />
          <Route path="/relatorios" element={<Relatorios />} />
          <Route path="/mapa-do-dia" element={<MapaChales />} />
          <Route path="/mapa-ocupacao" element={<OcupacaoMapa />} />
          <Route path="/hospedes" element={<Hospedes />} />
          <Route path="/manutencoes" element={<Manutencao />} />
          <Route path="/pacotes" element={<Pacotes />} />
          <Route path="/tarifas" element={<Tarifas />} />
          <Route path="/usuarios" element={<Usuarios />} />
          <Route path="/configuracoes" element={<Configuracoes />} />
        </Routes>
      </Box>
    </Box>
  );
}

// Componente que protege as rotas
function ProtectedRoute({ user, children }) {
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Router>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <Routes>
        <Route path="/login" element={!user ? <Login /> : <Navigate to="/" replace />} />
        <Route path="/*" element={<ProtectedRoute user={user}><MainLayout /></ProtectedRoute>} />
      </Routes>
    </Router>
  );
}

export default App;